import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextInput from './TextInput';
import './ColorPicker.css';

const basicColors = [
  '#d0021b', '#f5a623', '#f8e71c', '#8b572a',
  '#7ed321', '#417505', '#bd10e0', '#9013fe',
  '#4a90d9', '#50e3c2', '#b8e986', '#000000',
  '#4a4a4a', '#9b9b9b', '#ffffff',
];

const WIDTH = 214;
const HEIGHT = 150;

export default function ColorPicker({ color, onChange }) {
  const [selfColor, setSelfColor] = useState(transformColor('hex', color));
  const [inputColor, setInputColor] = useState(color);
  const innerDivRef = useRef(null);

  const emitOnChange = useCallback(
    (newColor) => {
      if (innerDivRef.current !== null && onChange) {
        onChange(newColor);
      }
    },
    [onChange]
  );

  const saturationPosition = useMemo(
    () => ({
      x: (selfColor.hsv.s / 100) * WIDTH,
      y: ((100 - selfColor.hsv.v) / 100) * HEIGHT,
    }),
    [selfColor.hsv.s, selfColor.hsv.v]
  );

  const huePosition = useMemo(
    () => ({ x: (selfColor.hsv.h / 360) * WIDTH }),
    [selfColor.hsv.h]
  );

  const onSetHex = (hex) => {
    setInputColor(hex);
    if (/^#[0-9A-Fa-f]{6}$/i.test(hex)) {
      const newColor = transformColor('hex', hex);
      setSelfColor(newColor);
      emitOnChange(newColor.hex);
    }
  };

  const onMoveSaturation = useCallback(({ x, y }) => {
    const newHsv = {
      ...selfColor.hsv,
      s: (x / WIDTH) * 100,
      v: 100 - (y / HEIGHT) * 100,
    };
    const newColor = transformColor('hsv', newHsv);
    setSelfColor(newColor);
    setInputColor(newColor.hex);
    emitOnChange(newColor.hex);
  }, [emitOnChange, selfColor.hsv]);

  const onMoveHue = useCallback(({ x }) => {
    const newHsv = { ...selfColor.hsv, h: (x / WIDTH) * 360 };
    const newColor = transformColor('hsv', newHsv);
    setSelfColor(newColor);
    setInputColor(newColor.hex);
    emitOnChange(newColor.hex);
  }, [emitOnChange, selfColor.hsv]);

  useEffect(() => {
    if (color === undefined) return;
    const newColor = transformColor('hex', color);
    setSelfColor(newColor);
    setInputColor(color);
  }, [color]);

  return (
    <div className="color-picker-wrapper" style={{ width: WIDTH }} ref={innerDivRef}>
      <TextInput label="Hex" onChange={onSetHex} value={inputColor} />
      <div className="color-picker-basic-color">
        {basicColors.map((basicColor) => (
          <button
            className={basicColor === selfColor.hex ? 'color-picker-basic-color-cell active' : 'color-picker-basic-color-cell'}
            key={basicColor}
            style={{ backgroundColor: basicColor }}
            onClick={() => {
              setInputColor(basicColor);
              const newColor = transformColor('hex', basicColor);
              setSelfColor(newColor);
              emitOnChange(newColor.hex);
            }}
          />
        ))}
      </div>
      <MoveWrapper
        className="color-picker-saturation"
        style={{ backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)` }}
        onChange={onMoveSaturation}
      >
        <div
          className="color-picker-saturation_cursor"
          style={{
            backgroundColor: selfColor.hex,
            left: saturationPosition.x,
            top: saturationPosition.y,
          }}
        />
      </MoveWrapper>
      <MoveWrapper className="color-picker-hue" onChange={onMoveHue}>
        <div
          className="color-picker-hue_cursor"
          style={{
            backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`,
            left: huePosition.x,
          }}
        />
      </MoveWrapper>
      <div
        className="color-picker-color"
        style={{ backgroundColor: selfColor.hex }}
      />
    </div>
  );
}

function MoveWrapper({ className, style, onChange, children }) {
  const divRef = useRef(null);
  const moveRef = useRef({ isMoving: false });

  const move = useCallback(
    (e) => {
      const div = divRef.current;
      if (div) {
        const { left, top, width, height } = div.getBoundingClientRect();
        const x = clamp(e.clientX - left, 0, width);
        const y = clamp(e.clientY - top, 0, height);
        onChange({ x, y });
      }
    },
    [onChange]
  );

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    moveRef.current.isMoving = true;
    move(e);
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (moveRef.current.isMoving) move(e);
    };
    const onMouseUp = () => {
      moveRef.current.isMoving = false;
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [move]);

  return (
    <div ref={divRef} className={className} style={style} onMouseDown={onMouseDown}>
      {children}
    </div>
  );
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function transformColor(format, color) {
  let hex, hsv;
  if (format === 'hex') {
    const hexStr = color.startsWith('#') ? color : `#${color}`;
    hex = hexStr;
    const r = parseInt(hexStr.slice(1, 3), 16) / 255;
    const g = parseInt(hexStr.slice(3, 5), 16) / 255;
    const b = parseInt(hexStr.slice(5, 7), 16) / 255;
    hsv = rgbToHsv(r, g, b);
  } else {
    hsv = { h: color.h, s: color.s, v: color.v };
    const rgb = hsvToRgb(color.h, color.s, color.v);
    hex = `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
  }
  return { hex, hsv };
}

function rgbToHsv(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : (d / max) * 100;
  const v = max * 100;
  if (d !== 0) {
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      case b: h = ((r - g) / d + 4) * 60; break;
      default: break;
    }
  }
  return { h, s, v };
}

function hsvToRgb(h, s, v) {
  h = h / 360;
  s = s / 100;
  v = v / 100;
  let r, g, b;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
    default: r = 0; g = 0; b = 0;
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

function toHex(n) {
  const hex = n.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}
